import classNames from "classnames";
import * as React from "react";

import styles from "./WorkspaceResizeHandle.module.css";

interface WorkspaceResizeHandleProps {
  onResize: (deltaX: number) => void;
}

/**
 * A resize handle for the workspace sidebar to change its width
 */
export function WorkspaceResizeHandle({
  onResize,
}: WorkspaceResizeHandleProps) {
  const currentX = React.useRef(0);
  const resizeHandleRef = React.useRef<HTMLDivElement>(null);

  const [isDragging, setIsDragging] = React.useState(false);

  const handlePointerDown = React.useCallback((event: React.PointerEvent) => {
    setIsDragging(true);
    currentX.current = event.clientX;

    // Forward all pointer events to this element
    resizeHandleRef.current?.setPointerCapture(event.pointerId);
    document.documentElement.classList.add(styles.disableUserInteraction);
  }, []);

  const handlePointerMove = React.useCallback(
    (event: React.PointerEvent) => {
      const deltaX = event.clientX - currentX.current;
      onResize(deltaX);

      currentX.current = event.clientX;
    },
    [onResize],
  );

  const handlePointerUp = React.useCallback((event: React.PointerEvent) => {
    setIsDragging(false);
    document.documentElement.classList.remove(styles.disableUserInteraction);
    resizeHandleRef.current?.releasePointerCapture(event.pointerId);
  }, []);

  return (
    <div
      aria-label="Resize sidebar width"
      className={classNames(styles.resizeHandle, {
        [styles.active]: isDragging,
      })}
      onPointerDown={handlePointerDown}
      onPointerMove={isDragging ? handlePointerMove : undefined}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      ref={resizeHandleRef}
      role="separator"
    >
      <div className={styles.resizeHandleIndicator} />
    </div>
  );
}
